import { Dispatch, SetStateAction, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  checkVenueSetupNeeded,
  errorToast,
  validateAndDecodeAuthToken,
  validatePassword,
} from '../utils/helper';
import { AuthService } from '../service/authService';
import { apiStatusCode, ErrorMessages, StorageKey } from '../utils/helper.type';
import {
  AcceptTermsResponse,
  AuthTokenResponse,
  ForgotPasswordResponse,
  NewPasswordResponse,
  ResetPasswordResponse,
  SendUserVerificationCodeResponse,
  VerifyOtpResponse,
  VerifyUserVerificationCodeResponse,
} from '../service/service.type';
import { route } from '../service/router';
import { AcceptTermsRequest, LoginResponse, UserType } from '@inplay/models';
import { useAuth } from '../service/context/authContext';
import { jwtDecode } from 'jwt-decode';
import { isUndefined } from 'lodash';

export const useLogoutData = (): {
  logout: (email: string) => Promise<void>;
  logoutLoading: boolean;
} => {
  const [logoutLoading, setLogoutLoading] = useState(false);
  const navigate = useNavigate();
  const { setAccessToken } = useAuth();
  const logout = async (email: string) => {
    if (!email) return;
    console.log(`[useLogoutData] Before calling logout...`);
    setLogoutLoading(true);
    try {
      const decodedData: AuthTokenResponse | null =
        validateAndDecodeAuthToken();

      // only call the logout api if the user is not a venue branch login
      // because logout invalides the refresh token
      if (decodedData?.type !== UserType.VENUE_BRANCH_LOGIN) {
        const authService: AuthService = new AuthService();
        await authService.logout(email);
      }

      localStorage.removeItem(StorageKey.REFRESH_TOKEN);
      localStorage.removeItem(StorageKey.ACCESS_TOKEN);
      localStorage.removeItem(StorageKey.MUST_ACCEPT_TNC);
      localStorage.removeItem(StorageKey.STAFF_PIN);
      localStorage.removeItem(StorageKey.IDP_TOKEN);
      localStorage.removeItem(StorageKey.VENUE_UNIT_DETAILS);
      setAccessToken(null);
      navigate(route.login);
    } catch (error: unknown) {
      console.error(`[useLogoutData][ERROR]`, { error });
      errorToast(error);
    } finally {
      console.log(`[useLogoutData] finally block`);
      setLogoutLoading(false);
    }
  };

  return { logout, logoutLoading };
};

export const useLoginData = (): {
  login: (
    email: string,
    password: string,
  ) => Promise<LoginResponse | undefined>;
  isLoading: boolean;
  error: string | null;
} => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { setAccessToken } = useAuth();
  const login = async (email: string, password: string) => {
    if (!email && !password) return;
    console.log(`[useLoginData] Before calling login...`);
    setIsLoading(true);
    setError(null);
    try {
      const authService: AuthService = new AuthService();
      const response: LoginResponse = await authService.login(email, password);

      if (response.hasMfa) {
        localStorage.setItem(StorageKey.SESSION_ID, response.sessionId);
      } else {
        const tokenData: AuthTokenResponse = jwtDecode(response.accessToken);

        localStorage.setItem(
          StorageKey.MUST_ACCEPT_TNC,
          JSON.stringify(response?.mustAcceptTnC),
        );

        // Update staff access token and staff refresh token in local storage if user is a venue branch login
        if (tokenData.type === UserType.VENUE_BRANCH_LOGIN) {
          localStorage.setItem(
            StorageKey.STAFF_ACCESS_TOKEN,
            response.accessToken,
          );
          if (response.refreshToken) {
            localStorage.setItem(
              StorageKey.STAFF_REFRESH_TOKEN,
              response.refreshToken,
            );
          }
        } else {
          localStorage.setItem(StorageKey.IDP_TOKEN, response?.idpToken);
          // Update access token and refresh token if user is any user other than venue branch login
          setAccessToken(response.accessToken);
          if (response.refreshToken) {
            localStorage.setItem(
              StorageKey.REFRESH_TOKEN,
              response.refreshToken,
            );
          }
        }
      }
      return response;
    } catch (error: unknown) {
      console.error(`[useLoginData][ERROR]`, { error });
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === apiStatusCode.UNAUTHORIZED) {
          setError(ErrorMessages.INVALID_CREDENTIALS);
        } else if (error?.response?.status === apiStatusCode.FORBIDDEN) {
          setError(ErrorMessages.DEACTIVATE);
        } else {
          setError(ErrorMessages.UNEXPECTED_ERROR);
        }
      } else {
        setError(ErrorMessages.UNEXPECTED_ERROR);
      }
    } finally {
      console.log(`[useLoginData] finally block`);
      setIsLoading(false);
    }
  };

  return { login, isLoading, error };
};

export const useForgotPasswordData = (): {
  forgotPassword: (email: string) => Promise<void>;
  isForgotLoading: boolean;
} => {
  const navigate = useNavigate();
  const [isForgotLoading, setIsForgotLoading] = useState<boolean>(false);

  const forgotPassword = async (email: string) => {
    if (!email) return;
    console.log(`[useForgotPasswordData] Before calling login...`);
    setIsForgotLoading(true);
    try {
      const authService: AuthService = new AuthService();
      const response: ForgotPasswordResponse =
        await authService.forgotPassword(email);

      if (response.statusCode === apiStatusCode.SUCCESS) {
        navigate(route.forgotPasswordSuccess, { state: { email } });
      }
    } catch (error: unknown) {
      console.error(`[useForgotPasswordData][ERROR]`, { error });
      errorToast(error);
    } finally {
      console.log(`[useForgotPasswordData] finally block`);
      setIsForgotLoading(false);
    }
  };

  return { forgotPassword, isForgotLoading };
};

export const useResetPasswordData = (): {
  resetPassword: (
    email: string,
    newPassword: string,
    confirmationCode: string,
  ) => Promise<void>;
  isResetLoading: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
} => {
  const navigate = useNavigate();
  const [isResetLoading, setIsResetLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const resetPassword = async (
    email: string,
    newPassword: string,
    confirmationCode: string,
  ) => {
    if (!email || !newPassword || !confirmationCode) {
      console.log('[useResetPasswordData] Missing field(s)...', {
        emailMissing: !email,
        password: !newPassword,
        codeMissing: !confirmationCode,
      });
      return;
    }
    if (!validatePassword(newPassword)) {
      setError(ErrorMessages.PASSWORD_INVALID);
      return;
    }
    console.log(`[useResetPasswordData] Before calling login...`);
    setIsResetLoading(true);
    try {
      const authService: AuthService = new AuthService();
      const response: ResetPasswordResponse = await authService.resetPassword(
        email,
        newPassword,
        confirmationCode,
      );

      if (response.statusCode === apiStatusCode.SUCCESS) {
        navigate(route.login, { state: { showResetSuccessToast: true } });
      }
    } catch (error: unknown) {
      console.error(`[useResetPasswordData][ERROR]`, { error });
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === apiStatusCode.UNAUTHORIZED) {
          setError(error?.response?.data.message);
        } else {
          setError(ErrorMessages.UNEXPECTED_ERROR);
        }
      } else {
        setError(ErrorMessages.UNEXPECTED_ERROR);
      }
    } finally {
      console.log(`[useResetPasswordData] finally block`);
      setIsResetLoading(false);
    }
  };

  return { resetPassword, isResetLoading, error, setError };
};

export const useOtpVerify = (): {
  verifyOtp: (email: string, sessionId: string, code: string) => Promise<void>;
  isVerifyLoading: boolean;
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;
} => {
  const navigate = useNavigate();
  const [isVerifyLoading, setIsVerifyLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { setAccessToken } = useAuth();

  const verifyOtp = async (
    email: string,
    sessionId: string,
    code: string,
  ): Promise<void> => {
    if (!email || !sessionId || !code) {
      console.log('[useOtpVerify] Missing field(s)...', {
        emailMissing: !email,
        sessionIdMissing: !sessionId,
        codeMissing: !code,
      });
      return;
    }
    console.log(`[useOtpVerify] Before calling verifyOtp...`);
    setIsVerifyLoading(true);
    setError(null);

    try {
      const authService = new AuthService();
      const response: VerifyOtpResponse = await authService.verifyOtp(
        email,
        sessionId,
        code,
      );

      if (response.statusCode === apiStatusCode.SUCCESS) {
        if (!response.data.hasMfa) {
          localStorage.setItem(
            StorageKey.MUST_ACCEPT_TNC,
            JSON.stringify(response.data?.mustAcceptTnC),
          );
          localStorage.setItem(StorageKey.IDP_TOKEN, response.data?.idpToken);
          const tokenData: AuthTokenResponse = jwtDecode(
            response.data.accessToken,
          );
          // store organization unit data
          localStorage.setItem(
            StorageKey.ORGANIZATION_UNIT_DATA,
            JSON.stringify(response.data.organisationUnitDatas),
          );

          // Update staff access token and staff refresh token in local storage if user is a venue branch login
          if (tokenData.type === UserType.VENUE_BRANCH_LOGIN) {
            localStorage.setItem(
              StorageKey.STAFF_ACCESS_TOKEN,
              response.data.accessToken,
            );
            if (response.data.refreshToken) {
              localStorage.setItem(
                StorageKey.STAFF_REFRESH_TOKEN,
                response.data.refreshToken,
              );
            }
          } else {
            // Update access token and refresh token if user is any user other than venue branch login
            setAccessToken(response.data.accessToken);
            if (response.data.refreshToken) {
              localStorage.setItem(
                StorageKey.REFRESH_TOKEN,
                response.data.refreshToken,
              );
            }
          }
          // Check if user is venue admin/manager and if venue setup is needed
          localStorage.setItem(
            StorageKey.VENUE_UNIT_DETAILS,
            JSON.stringify(response?.data?.venueUnitDetails),
          );
          if (response.data.onboardingPending) {
            localStorage.setItem(
              StorageKey.ACCESS_TOKEN,
              JSON.stringify(response.data.accessToken),
            );
            setAccessToken(response.data.accessToken);
            navigate(route.onboarding);
          } else {
            if (
              (tokenData?.type === UserType.VENUE_ADMIN ||
                tokenData?.type === UserType.VENUE_MANAGER) &&
              response?.data?.venueUnitDetails &&
              checkVenueSetupNeeded(response?.data?.venueUnitDetails)
            ) {
              navigate(route.venueSetup);
            } else {
              navigate(route.dashboard);
            }
          }
        }
      }
    } catch (error: unknown) {
      console.error(`[useOtpVerify][ERROR]`, { error });
      if (axios.isAxiosError(error)) {
        if (
          error.response &&
          error.response.status === apiStatusCode.UNAUTHORIZED
        ) {
          setError(ErrorMessages.INCORRECT_OTP);
        } else {
          setError(ErrorMessages.UNEXPECTED_ERROR);
        }
      }
    } finally {
      console.log(`[useOtpVerify] finally block`);
      setIsVerifyLoading(false);
    }
  };

  return {
    verifyOtp,
    isVerifyLoading,
    error,
    setError,
  };
};

export const useNewPasswordData = (): {
  newPassword: (email: string, password: string, i: string) => Promise<void>;
  isNewPasswordLoading: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
} => {
  const navigate = useNavigate();
  const [isNewPasswordLoading, setIsNewPasswordLoading] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const newPassword = async (
    email: string,
    password: string,
    activationCode: string,
  ) => {
    if (!email || !password || !activationCode) {
      console.log('[useResetPasswordData] Missing field(s)...', {
        emailMissing: !email,
        password: !password,
        codeMissing: !activationCode,
      });
      return;
    }

    if (!validatePassword(password)) {
      setError(ErrorMessages.PASSWORD_INVALID);
      return;
    }

    console.log(`[useNewPasswordData] Before calling login...`);
    setIsNewPasswordLoading(true);
    try {
      const authService: AuthService = new AuthService();
      const response: NewPasswordResponse = await authService.newPassword(
        email,
        password,
        activationCode,
      );

      if (response.statusCode === apiStatusCode.SUCCESS) {
        if (response.data.hasMfa) {
          navigate(route.verifyOtp, { state: { email } });
          localStorage.setItem(StorageKey.SESSION_ID, response.data.sessionId);
        } else {
          navigate(route.login, {
            state: { showNewPasswordSuccessToast: true },
          });
        }
      }
    } catch (error: unknown) {
      console.error(`[useNewPasswordData][ERROR]`, { error });

      if (axios.isAxiosError(error)) {
        if (error?.response?.status === apiStatusCode.UNAUTHORIZED) {
          setError(error?.response?.data.message);
        } else {
          setError(ErrorMessages.UNEXPECTED_ERROR);
        }
      } else {
        setError(ErrorMessages.UNEXPECTED_ERROR);
      }
    } finally {
      console.log(`[useResetPasswordData] finally block`);
      setIsNewPasswordLoading(false);
    }
  };

  return { newPassword, isNewPasswordLoading, error, setError };
};

export const useAcceptTermsData = (): {
  acceptTerms: (acceptTermsRequest: AcceptTermsRequest) => Promise<void>;
  isAcceptLoading: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
} => {
  const [isAcceptLoading, setIsAcceptLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const acceptTerms = async (acceptTermsRequest: AcceptTermsRequest) => {
    if (!acceptTermsRequest.version) {
      console.log('[useAcceptTermsData] Missing field(s)...', {
        version: acceptTermsRequest.version,
      });
      return;
    }
    console.log(`[useAcceptTermsData] Before calling login...`);
    setIsAcceptLoading(true);
    try {
      const authService: AuthService = new AuthService();
      const response: AcceptTermsResponse =
        await authService.acceptTerms(acceptTermsRequest);

      if (response.statusCode === apiStatusCode.SUCCESS) {
        localStorage.setItem(StorageKey.MUST_ACCEPT_TNC, JSON.stringify(false));
      }
    } catch (error: unknown) {
      console.error(`[useAcceptTermsData][ERROR]`, { error });
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === apiStatusCode.UNAUTHORIZED) {
          setError(error?.response?.data.message);
        } else {
          setError(ErrorMessages.UNEXPECTED_ERROR);
        }
      } else {
        setError(ErrorMessages.UNEXPECTED_ERROR);
      }
    } finally {
      console.log(`[useAcceptTermsData] finally block`);
      setIsAcceptLoading(false);
    }
  };

  return { acceptTerms, isAcceptLoading, error, setError };
};

export const useChangeProfilePassword = (): {
  changeProfilePassword: (
    currentPassword: string,
    newPassword: string,
    idpToken: string,
    shouldNavigateToProfilePage?: boolean,
    userEmail?: string,
  ) => Promise<ForgotPasswordResponse | undefined>;
  changeProfilePasswordLoading: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
} => {
  const [changeProfilePasswordLoading, setChangeProfilePasswordLoading] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const changeProfilePassword = async (
    currentPassword: string,
    newPassword: string,
    idpToken: string,
    shouldNavigateToProfilePage = true,
    userEmail?: string,
  ) => {
    if (!currentPassword && !newPassword! && idpToken) {
      console.log('[useChangeProfilePassword] Missing field(s)...', {
        currentPassword: !currentPassword,
        newPassword: !newPassword,
        idpToken: !idpToken,
      });
      setError(null);
      return;
    }
    if (!validatePassword(newPassword)) {
      setError(ErrorMessages.PASSWORD_INVALID);
      return;
    }
    console.log(
      `[useProfileChangePassword] Before calling change password api...`,
    );
    setChangeProfilePasswordLoading(true);
    try {
      setError(null);
      const authService: AuthService = new AuthService();

      const userChangeProfilePasswordResponse: ForgotPasswordResponse =
        await authService.changeProfilePassword(
          currentPassword,
          newPassword,
          idpToken,
          userEmail,
        );

      if (!isUndefined(userChangeProfilePasswordResponse)) {
        console.log(
          `[useProfileChangePassword] response `,
          userChangeProfilePasswordResponse,
        );
        if (
          userChangeProfilePasswordResponse?.statusCode ===
          apiStatusCode.SUCCESS
        ) {
          if (shouldNavigateToProfilePage) {
            navigate(route.profile, {
              state: { showChangePasswordSuccessToast: true },
            });
          }
        }
      }
      return userChangeProfilePasswordResponse;
    } catch (error: unknown) {
      console.error(`[useProfileChangePassword][ERROR]`, { error });
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === apiStatusCode.UNAUTHORIZED) {
          setError(error?.response?.data.message);
        } else if (error?.response?.status === apiStatusCode.NOT_FOUND) {
          setError(error?.response?.data.message);
        }
      } else {
        setError(ErrorMessages.UNEXPECTED_ERROR);
      }
    } finally {
      console.log(`[useChangeProfilePassword] finally block`);
      setChangeProfilePasswordLoading(false);
    }
  };

  return {
    changeProfilePassword,
    changeProfilePasswordLoading,
    error,
    setError,
  };
};

export const useSendUserVerificationCode = (): {
  sendOtp: (email: string, userId: string) => Promise<void>;
  sendOtpLoading: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  sessionId: string | null;
} => {
  const [sendOtpLoading, setSendOtpLoading] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sendOtp = async (email: string, userId: string) => {
    if (!email || !userId) {
      console.log('[useSendUserVerificationCode] Missing field(s)...', {
        emailMissing: !email,
        userId: !userId,
      });
      return;
    }

    console.log(`[useSendUserVerificationCode][Entry]: `, { email, userId });
    setSendOtpLoading(true);
    try {
      const authService: AuthService = new AuthService();
      const response: SendUserVerificationCodeResponse =
        await authService.sendUserVerificationCode(email, userId);
      if (response.statusCode === apiStatusCode.SUCCESS) {
        console.log(`[useSendUserVerificationCode][SUCCESS]: `, response);
        setSessionId(response.data.session);
      }
    } catch (error: unknown) {
      console.error(`[useSendUserVerificationCode][ERROR]`, { error });

      if (axios.isAxiosError(error)) {
        if (error?.response?.status === apiStatusCode.UNAUTHORIZED) {
          setError(error?.response?.data.message);
        } else {
          setError(ErrorMessages.UNEXPECTED_ERROR);
        }
      } else {
        setError(ErrorMessages.UNEXPECTED_ERROR);
      }
    } finally {
      console.log(`[useSendUserVerificationCode] finally block`);
      setSendOtpLoading(false);
    }
  };

  return { sendOtp, sendOtpLoading, error, setError, sessionId };
};

export const useVerifyUserVerificationCode = (): {
  verifyOtp: (
    code: string,
    session: string,
    userName: string,
  ) => Promise<VerifyUserVerificationCodeResponse | undefined>;
  verifyOtpLoading: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  verified: boolean;
  attemptsRemaining: number | null;
  expired: boolean;
} => {
  const [verifyOtpLoading, setVerifyOtpLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState<boolean>(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [expired, setExpired] = useState<boolean>(false);

  const verifyOtp = async (
    code: string,
    session: string,
    userid: string,
  ): Promise<VerifyUserVerificationCodeResponse | undefined> => {
    if (!code || !session || !userid) {
      console.log('[useVerifyUserVerificationCode] Missing field(s)...', {
        codeMissing: !code,
        sessionMissing: !session,
        userIdMissing: !userid,
      });
      return;
    }

    console.log(`[useVerifyUserVerificationCode][Entry]: `, {
      code,
      session,
      userid,
    });
    setVerifyOtpLoading(true);
    try {
      const authService: AuthService = new AuthService();
      const response: VerifyUserVerificationCodeResponse =
        await authService.VerifyUserVerificationCode(code, session, userid);
      if (response.statusCode === apiStatusCode.SUCCESS) {
        console.log(`[useVerifyUserVerificationCode][SUCCESS]: `, response);
        setVerified(true);
        return response;
      }
    } catch (error: unknown) {
      console.error(`[useVerifyUserVerificationCode][ERROR]`, { error });
      setVerified(false);
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === apiStatusCode.UNAUTHORIZED) {
          const data = error?.response?.data;
          setError(data.message);
          setAttemptsRemaining(data.data?.attemptsRemaining ?? null);
          setExpired(data.data?.expired ?? false);
        } else {
          setError(ErrorMessages.UNEXPECTED_ERROR);
        }
      } else {
        setError(ErrorMessages.UNEXPECTED_ERROR);
      }
    } finally {
      console.log(`[useVerifyUserVerificationCode] finally block`);
      setVerifyOtpLoading(false);
    }
  };

  return { verifyOtp, verifyOtpLoading, error, setError, verified, attemptsRemaining, expired };
};
